import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Alert, Box, Paper, Stack, Typography } from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';
const REFRESH_INTERVAL_MS = 30000;

const currency = (value) => {
  if (!Number.isFinite(value)) {
    return '-';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Compact, thinkorswim-style account list for the Stocks sidebar (Portfolio tab only).
const PaperAccountSummary = () => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const runtimeEnv = window.__RUNTIME_ENV__ || {};
  const authAudience = String(runtimeEnv.AUTH0_AUDIENCE || process.env.REACT_APP_AUTH0_AUDIENCE || '').trim();
  const userId = isAuthenticated && user?.sub ? user.sub : 'anonymous';
  const userEmail = isAuthenticated && user?.email ? String(user.email).trim().toLowerCase() : '';

  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');

  const fetchSummary = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    try {
      const headers = {
        'X-User-Id': userId,
        ...(userEmail ? { 'X-User-Email': userEmail } : {}),
      };
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            scope: 'openid profile email',
            ...(authAudience ? { audience: authAudience } : {}),
          },
        });
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      } catch (tokenError) {
        // Fall through with unauthenticated headers; request will surface an error below.
      }

      const response = await fetch(`${API_BASE_URL}/api/paper/portfolio/summary`, { headers });
      if (!response.ok) {
        throw new Error(`Account summary request failed (${response.status})`);
      }
      const payload = await response.json();
      setSummary(payload);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load account summary');
    }
  }, [authAudience, getAccessTokenSilently, isAuthenticated, userEmail, userId]);

  useEffect(() => {
    fetchSummary();
    const intervalId = window.setInterval(fetchSummary, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [fetchSummary]);

  if (!isAuthenticated) {
    return null;
  }

  const metrics = summary?.metrics || {};
  const cashBalance = Number(summary?.account?.cash_balance);

  const rows = [
    { label: 'Cash Balance', value: currency(cashBalance), color: cashBalance < 0 ? 'error.main' : 'text.primary' },
    { label: 'Total Equity', value: currency(Number(metrics.equity)) },
    { label: 'Market Value', value: currency(Number(metrics.market_value)) },
    {
      label: 'Total P/L',
      value: currency(Number(metrics.total_pnl)),
      color: Number(metrics.total_pnl) >= 0 ? 'success.main' : 'error.main',
    },
    { label: 'Buying Power', value: currency(Number(metrics.buying_power)) },
    { label: 'Long Exposure', value: currency(Number(metrics.long_market_value)) },
    { label: 'Short Exposure', value: currency(Number(metrics.short_market_value)) },
    {
      label: 'Maintenance Req.',
      value: currency(Number(metrics.maintenance_requirement)),
      color: metrics.margin_call ? 'error.main' : 'text.primary',
    },
  ];

  return (
    <Paper sx={{ p: 1.5, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Account Summary
      </Typography>
      {error && (
        <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
          {error}
        </Alert>
      )}
      {Boolean(metrics.margin_call) && (
        <Alert severity="error" sx={{ mb: 1, py: 0 }}>
          Margin call
        </Alert>
      )}
      <Stack spacing={0}>
        {rows.map((row, index) => (
          <Box
            key={row.label}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 0.6,
              borderBottom: index < rows.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary">{row.label}</Typography>
            <Typography variant="body2" fontWeight={700} color={row.color || 'text.primary'}>
              {row.value}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default PaperAccountSummary;
